import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/DefaultLayout";
import { useNavigate, useLocation } from "react-router-dom";

import api from "../../utils/api";
import "../../styles/assessmentsetup.css";

const DIFFICULTY_OPTIONS = ["LOW", "MED", "HIGH"];
const QUESTION_TYPES = [
  "MULTIPLE_CHOICE",
  "TRUE_FALSE",
  "FILL_IN_THE_BLANK",
  "MATCHING",
  "OPEN_ENDED",
  "QUESTION_TABLE",
  "MATHS_CALCULATION"
];

const AssessmentSetup = () => {
  const navigate = useNavigate();

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState([]);

  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [timerDuration, setTimerDuration] = useState(30);
  const [courseName, setCourseName] = useState("");

  const [presets, setPresets] = useState([]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentId = queryParams.get("id");
  const isEditMode = Boolean(assessmentId);
  const username = sessionStorage.getItem("username") || "anonymous";

  const [initialSubjectIds, setInitialSubjectIds] = useState([]);
  const [initialChapterIds, setInitialChapterIds] = useState([]);



  useEffect(() => {
    api.get("/class-standards").then(res => setClasses(res.data));
  }, []);

  useEffect(() => {
    if (assessmentId) {
      api.get(`/assessment-courses/${assessmentId}`).then(res => {
        const data = res.data;

        setCourseName(data.courseName);
        setSelectedClass(String(data.classId));
        setNumberOfQuestions(data.numberOfQuestions);
        setTimerDuration(data.timerDuration);
        setSelectedDifficulties(data.difficultyLevels?.split(",") || []);
        setSelectedQuestionTypes(data.questionTypes?.split(",") || []);

        setInitialSubjectIds(data.subjectIds?.split(",").map(Number) || []);
        setInitialChapterIds(data.chapterIds?.split(",").map(Number) || []);
      });
    }
  }, [assessmentId]);

  useEffect(() => {
    if (selectedClass) {
      api.get(`/subjects?classId=${selectedClass}`).then(res => {
        setSubjects(res.data);

        // If editing, set subjects after fetch
        if (initialSubjectIds.length > 0) {
          setSelectedSubjects(initialSubjectIds);
          setInitialSubjectIds([]); // Clear to avoid re-trigger
          setPresets([]);
        }
      });
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSubjects.length > 0) {
      Promise.all(selectedSubjects.map(id => api.get(`/chapters?subjectId=${id}`)))
        .then(responses => {
          const allChapters = responses.flatMap(res => res.data);
          setChapters(allChapters);
          generatePresets(allChapters);

          // If editing, set chapters after fetch
          if (initialChapterIds.length > 0) {
            const validChapterIds = allChapters
              .filter(ch => initialChapterIds.includes(ch.id))
              .map(ch => ch.id);
            setSelectedChapters(validChapterIds);
            setInitialChapterIds([]); // Clear to avoid re-trigger
          }
        });
    }
  }, [selectedSubjects]);





  // useEffect(() => {
  //   if (selectedClass) {
  //     api.get(`/subjects?classId=${selectedClass}`).then(res => {
  //       setSubjects(res.data);
  //       setSelectedSubjects([]);
  //       setChapters([]);
  //       setSelectedChapters([]);
  //       setPresets([]);
  //     });
  //   }
  // }, [selectedClass]);

  // useEffect(() => {
  //   if (selectedSubjects.length > 0) {
  //     Promise.all(selectedSubjects.map(id => api.get(`/chapters?subjectId=${id}`)))
  //       .then(responses => {
  //         const allChapters = responses.flatMap(res => res.data);
  //         setChapters(allChapters);
  //         setSelectedChapters([]);
  //         generatePresets(allChapters);
  //       });
  //   }
  // }, [selectedSubjects]);

  const toggleSelection = (value, setter, current) => {
    setter(current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]);
  };

  const generatePresets = (availableChapters) => {
    const chapterIds = availableChapters.map(ch => ch.id);
    const presetsList = [
      {
        name: "Quick Practice",
        chapters: chapterIds.slice(0, 2),
        difficulties: ["LOW"],
        types: ["MULTIPLE_CHOICE"],
        questions: 5,
        timer: 10
      },
      {
        name: "Mixed Challenge",
        chapters: chapterIds.slice(0, 4),
        difficulties: ["MED", "HIGH"],
        types: ["MULTIPLE_CHOICE", "TRUE_FALSE"],
        questions: 10,
        timer: 20
      },
      {
        name: "Full Test",
        chapters: chapterIds,
        difficulties: ["LOW", "MED", "HIGH"],
        types: QUESTION_TYPES,
        questions: 20,
        timer: 40
      }
    ];
    setPresets(presetsList);
  };

  const applyPreset = (preset) => {
    setSelectedChapters(preset.chapters);
    setSelectedDifficulties(preset.difficulties);
    setSelectedQuestionTypes(preset.types);
    setNumberOfQuestions(preset.questions);
    setTimerDuration(preset.timer);
  };

  const createAssessment = () => {
    if (!selectedClass || selectedSubjects.length === 0 || selectedChapters.length === 0 || !courseName.trim()) {
      alert("Please fill in all required fields including Course Name.");
      return;
    }

    const payload = {
      courseName: courseName.trim(),
      classId: Number(selectedClass),
      subjectIds: selectedSubjects.join(","),
      chapterIds: selectedChapters.join(","),
      difficultyLevels: selectedDifficulties.join(","),
      questionTypes: selectedQuestionTypes.join(","),
      numberOfQuestions,
      timerDuration,
      createdBy: username,
      createdDate: new Date().toISOString()
    };

    const request = assessmentId
      ? api.put(`/assessment-courses/${assessmentId}`, { ...payload, id: Number(assessmentId) })
      : api.post("/assessment-courses", payload);

    request
      .then(response => {
        console.log("Assessment config saved:", response.data);
        navigate("/assessments", {
          state: {
            courseName: payload.courseName,
            subjectName: subjects.filter(s => selectedSubjects.includes(s.id)).map(s => s.name).join(", "),
            chapterNames: chapters.filter(c => selectedChapters.includes(c.id)).map(c => c.name),
            questionsData: [],
            timerDuration: payload.timerDuration
          }
        });
      })
      .catch(error => {
        console.error("Failed to save assessment config:", error);
        alert("Something went wrong while saving the assessment.");
      });
  };

  const exitAssessment = () => {
    if (window.confirm("Are you sure you want to exit the assessment?")) {
      navigate("/assessments");
    }
  };


  const startAssessment = () => {
    if (!selectedClass || selectedSubjects.length === 0 || selectedChapters.length === 0 || !courseName.trim()) {
      alert("Please fill in all required fields including Course Name.");
      return;
    }

    api.get("/questions", {
      params: {
        classId: selectedClass,
        subjectIds: selectedSubjects.join(","),
        chapterIds: selectedChapters.join(","),
        difficultyLevels: selectedDifficulties.join(","),
        questionTypes: selectedQuestionTypes.join(","),
        limit: numberOfQuestions,
      },
    }).then(res => {
      const subjectNames = subjects.filter(s => selectedSubjects.includes(s.id)).map(s => s.name);
      const chapterNames = chapters.filter(c => selectedChapters.includes(c.id)).map(c => c.name);

      navigate("/assessment", {
        state: {
          courseName,
          subjectName: subjectNames.join(", "),
          chapterNames,
          questionsData: res.data,
          timerDuration,
        },
      });
    });
  };

  const formatPreviewList = (items) => {
    if (items.length <= 2) return items.join(", ");

    const visible = items.slice(0, 2).join(", ");
    const hiddenItems = items.slice(2);

    return (
      <span className="preview-multivalue">
        {visible},{" "}
        <span className="preview-tooltip-wrapper">
          <span className="preview-tooltip-trigger">+{items.length - 2} more</span>
          <div className="preview-tooltip-box">
            <ul>
              {items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </span>
      </span>
    );
  };



  return (
    <Layout>
      <div className="assessment-setup-wrapper">
        {/* Left: Form */}
        <div className="assessment-setup-form">
          <h2>📝 Build Your Custom Assessment</h2>

          <div className="setup-section">
            <h4>🎓 Select Class</h4>
            <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
              <option value="">-- Choose Class --</option>
              {classes.map(cls => (
                <option key={cls.id} value={String(cls.id)}>{cls.name}</option>
              ))}
            </select>
          </div>

          {subjects.length > 0 && (
            <div className="setup-section">
              <h4>📚 Select Subjects</h4>
              <div className="checkbox-grid">
                {subjects.map(sub => (
                  <label key={sub.id}>
                    <input
                      type="checkbox"
                      checked={selectedSubjects.includes(sub.id)}
                      onChange={() => toggleSelection(sub.id, setSelectedSubjects, selectedSubjects)}
                    />
                    {sub.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {selectedClass && selectedSubjects.length > 0 && (
            <>
              {chapters.length > 0 && (
                <div className="setup-section">
                  <h4>📖 Select Chapters</h4>
                  <div className="checkbox-grid">
                    {chapters.map(ch => (
                      <label key={ch.id}>
                        <input
                          type="checkbox"
                          checked={selectedChapters.includes(ch.id)}
                          onChange={() => toggleSelection(ch.id, setSelectedChapters, selectedChapters)}
                        />
                        {ch.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="setup-section">
                <h4>🎯 Difficulty Levels</h4>
                <div className="checkbox-grid">
                  {DIFFICULTY_OPTIONS.map(level => (
                    <label key={level}>
                      <input
                        type="checkbox"
                        checked={selectedDifficulties.includes(level)}
                        onChange={() => toggleSelection(level, setSelectedDifficulties, selectedDifficulties)}
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>

              <div className="setup-section">
                <h4>🧠 Question Types</h4>
                <div className="checkbox-grid">
                  {QUESTION_TYPES.map(type => (
                    <label key={type}>
                      <input
                        type="checkbox"
                        checked={selectedQuestionTypes.includes(type)}
                        onChange={() => toggleSelection(type, setSelectedQuestionTypes, selectedQuestionTypes)}
                      />
                      {type.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
              </div>

              <div className="setup-section">
                <h4>⚙️ Settings</h4>
                <label>
                  Number of Questions:
                  <input
                    type="number"
                    value={numberOfQuestions}
                    onChange={(e) => setNumberOfQuestions(Number(e.target.value))}
                    min={1}
                  />
                </label>
                <label>
                  Timer Duration (minutes):
                  <input
                    type="number"
                    value={timerDuration}
                    onChange={(e) => setTimerDuration(Number(e.target.value))}
                    min={1}
                  />
                </label>
              </div>


            </>
          )}
        </div>

        {/* Right: Preview + Presets */}
        <div className="assessment-preview-panel">
          <h4>🧾 Preview Your Selection</h4>
          <ul className="preview-list">
            <li><strong>Course:</strong> {courseName || "None"}</li>
            <li><strong>Class:</strong> {classes.find(c => String(c.id) === selectedClass)?.name || "None"}</li>
            <li><strong>Subjects:</strong> {
              selectedSubjects.length > 0
                ? formatPreviewList(subjects.filter(s => selectedSubjects.includes(s.id)).map(s => s.name))
                : "None"
            }</li>
            <li><strong>Chapters:</strong> {
              selectedChapters.length > 0
                ? formatPreviewList(chapters.filter(c => selectedChapters.includes(c.id)).map(c => c.name))
                : "None"
            }</li>
            <li><strong>Difficulty:</strong> {
              selectedDifficulties.length > 0
                ? formatPreviewList(selectedDifficulties)
                : "None"
            }</li>
            <li><strong>Question Types:</strong> {
              selectedQuestionTypes.length > 0
                ? formatPreviewList(selectedQuestionTypes.map(q => q.replace(/_/g, " ")))
                : "None"
            }</li>
            <li><strong>Questions:</strong> {numberOfQuestions}</li>
            <li><strong>Timer:</strong> {timerDuration} min</li>
          </ul>


          <>
            {/* Course Name Input */}
            <div className="setup-section">
              <h4>📘 Course Name <span style={{ color: "red" }}>*</span></h4>
              <input
                type="text"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="Enter course name"
                required
              />
            </div>

            <div className="action-buttons">
              <div className="setup-actions">

                {isEditMode ? (
                  <>
                    <button className="button button-primary" onClick={exitAssessment}>
                      Cancel
                    </button>
                    <button className="button button-secondary" onClick={createAssessment}>
                      Modify Assessment
                    </button>
                  </>
                ) : (
                  <>
                    <button className="button button-primary" onClick={startAssessment}>
                      Start Assessment
                    </button>
                    <button className="button button-secondary" onClick={createAssessment}>
                      Create Assessment
                    </button>
                  </>
                )}
              </div>

            </div>



          </>

          {presets.length > 0 && (
            <div className="preset-panel">
              <h4>⚡ Presets</h4>
              <ul className="preset-list">
                {presets.map((preset, index) => (
                  <li key={index}>
                    <button className="preset-btn" onClick={() => applyPreset(preset)}>
                      {preset.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AssessmentSetup;
